import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { RoleMatrixApi } from '../tokens/role-matrix.token';
import { RoleMatrix } from '../models/role-matrix.model';
import { normaliseRole } from '../models/sme-permission.model';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-only `RoleMatrixApi` (EM-937 / F3 + F6) — `GET /services/embedded/api/authz/role-matrix`.
 *
 * The single portal home for the authoritative role→permission definition (FR-4.6). Every screen
 * that needs "what may this role do?" injects `ROLE_MATRIX` and reads this; none holds a copy.
 *
 * The payload is static policy, so the in-flight observable is `shareReplay`-ed and the resolved
 * value cached for the session — concurrent callers share one request, later callers pay nothing.
 *
 * Rule 07 / 10 — no `Mock*`; Playwright intercepts at the HTTP layer with `page.route()`.
 */
@Injectable()
export class RoleMatrixService implements RoleMatrixApi {
  private readonly http = inject(HttpClient);

  private readonly matrixSig = signal<RoleMatrix | null>(null);
  private readonly errorSig = signal<string | null>(null);

  /** The cached in-flight/resolved request, so N callers cause exactly one HTTP call. */
  private inFlight: Observable<RoleMatrix> | null = null;

  readonly matrix = this.matrixSig.asReadonly();
  readonly error = this.errorSig.asReadonly();

  load(): Observable<RoleMatrix> {
    const cached = this.matrixSig();
    if (cached) {
      return of(cached);
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.http.get<RoleMatrix>(API_PATHS.AUTHZ_ROLE_MATRIX).pipe(
      // Normalise role casing ONCE, here at the REST boundary (Rule 17). Downstream code compares
      // canonical uppercase values only — under multi-role, a casing mismatch on a role ARRAY
      // silently yields an empty union and the rep sees nothing.
      tap((matrix) => {
        const normalised: RoleMatrix = {
          ...matrix,
          roles: (matrix.roles ?? []).map((r) => ({ ...r, role: normaliseRole(r.role) ?? r.role })),
        };
        this.matrixSig.set(normalised);
        this.errorSig.set(null);
      }),
      catchError((err: HttpErrorResponse) => {
        // Rule 09 — keep the backend's own reason; the caller renders this string directly.
        this.errorSig.set(extractBackendErrorMessage(err, 'Could not load the role definition.'));
        // Allow a later retry rather than caching the failure forever.
        this.inFlight = null;
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlight;
  }
}
